export interface QrSlipBillItem {
  studentInvoiceId: number;
  invoidId: string;
  billId: string;
  content: string;
  cost: number;
  month: number;
  year: number;
}

export interface QrSlipRenderData {
  schoolName?: string;
  studentId: number;
  studentName: string;
  studentCode: string;
  classYearName: string;
  schoolYear: string;
  month: number;
  year: number;
  bills: QrSlipBillItem[];
  totalCost: number;
  qrBase64: string;
}

export interface ZipEntryFile {
  fileName: string;
  data: Uint8Array;
}

const formatCurrencyVnd = (amount: number): string =>
  `${new Intl.NumberFormat('vi-VN').format(amount || 0)} VNĐ`;

export const sanitizeFileNameSegment = (input: string): string => {
  if (!input) return 'HocSinh';
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

export const buildStudentSlipFileName = (
  data: QrSlipRenderData,
  index?: number
): string => {
  const prefix =
    typeof index === 'number' ? `${String(index + 1).padStart(2, '0')}_` : '';
  const cls = sanitizeFileNameSegment(data.classYearName || 'Lop');
  const name = sanitizeFileNameSegment(data.studentName || 'HocSinh');
  const period = `T${String(data.month).padStart(2, '0')}-${data.year}`;
  return `${prefix}QR_${cls}_${name}_${period}_${data.totalCost || 0}d.png`;
};

const wrapCanvasText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] => {
  const clean = (text || 'Khoản thu').trim();
  if (!clean) return ['Khoản thu'];
  const words = clean.split(/\s+/);
  const lines: string[] = [];
  let currentLine = words[0] || '';

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const testLine = `${currentLine} ${word}`;
    if (ctx.measureText(testLine).width <= maxWidth) {
      currentLine = testLine;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  if (currentLine) {
    lines.push(currentLine);
  }
  return lines.length > 0 ? lines : ['Khoản thu'];
};

const loadImageFromBase64 = (qrBase64: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    const cleanSrc = qrBase64.startsWith('data:image')
      ? qrBase64
      : `data:image/png;base64,${qrBase64}`;
    img.src = cleanSrc;
  });

export const renderQrPaymentSlipToBlob = async (
  data: QrSlipRenderData
): Promise<Blob> => {
  const scale = 2;
  const cardWidth = 580;
  const paddingX = 24;
  const contentWidth = cardWidth - paddingX * 2;
  const fontFamily = '"Segoe UI", Arial, sans-serif';

  const measureCanvas = document.createElement('canvas');
  const measureCtx = measureCanvas.getContext('2d');
  if (!measureCtx) {
    throw new Error('Không thể khởi tạo Canvas 2D');
  }

  measureCtx.font = `500 13px ${fontFamily}`;
  const descColWidth = 340;
  const wrappedBills = data.bills.map((bill) => {
    const lines = wrapCanvasText(
      measureCtx,
      bill.content || bill.invoidId || 'Khoản thu',
      descColWidth
    );
    const rowHeight = Math.max(34, lines.length * 18 + 14);
    return { bill, lines, rowHeight };
  });

  const tableBodyHeight = wrappedBills.reduce(
    (sum, item) => sum + item.rowHeight,
    0
  );
  const headerHeight = 64;
  const studentBoxHeight = 76;
  const tableHeaderHeight = 34;
  const tableTotalHeight = 38;
  const qrSectionHeight = 250;
  const totalHeight =
    headerHeight +
    14 +
    studentBoxHeight +
    14 +
    tableHeaderHeight +
    tableBodyHeight +
    tableTotalHeight +
    16 +
    qrSectionHeight +
    20;

  const canvas = document.createElement('canvas');
  canvas.width = cardWidth * scale;
  canvas.height = totalHeight * scale;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Không thể khởi tạo Canvas 2D');
  }
  ctx.scale(scale, scale);

  // Background & border
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, cardWidth, totalHeight);
  ctx.strokeStyle = '#d9d9d9';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(1, 1, cardWidth - 2, totalHeight - 2);

  // Header
  ctx.fillStyle = '#f5f5f5';
  ctx.fillRect(1, 1, cardWidth - 2, headerHeight);
  ctx.strokeStyle = '#d9d9d9';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, headerHeight);
  ctx.lineTo(cardWidth, headerHeight);
  ctx.stroke();

  ctx.fillStyle = '#111827';
  ctx.font = `700 17px ${fontFamily}`;
  ctx.textAlign = 'center';
  ctx.fillText('PHIẾU BÁO THU HỌC PHÍ', cardWidth / 2, 30);

  ctx.fillStyle = '#4b5563';
  ctx.font = `600 13px ${fontFamily}`;
  ctx.fillText(
    `Tháng ${String(data.month).padStart(2, '0')}/${data.year}`,
    cardWidth / 2,
    50
  );

  // Student Info
  let cursorY = headerHeight + 14;
  ctx.fillStyle = '#fafafa';
  ctx.fillRect(paddingX, cursorY, contentWidth, studentBoxHeight);
  ctx.strokeStyle = '#e5e7eb';
  ctx.strokeRect(paddingX, cursorY, contentWidth, studentBoxHeight);

  ctx.textAlign = 'left';
  ctx.fillStyle = '#111827';
  ctx.font = `700 14px ${fontFamily}`;
  ctx.fillText(`Học sinh: ${data.studentName || '—'}`, paddingX + 12, cursorY + 26);

  ctx.fillStyle = '#374151';
  ctx.font = `500 13px ${fontFamily}`;
  ctx.fillText(
    `Lớp: ${data.classYearName || '—'}    |    Mã HS: ${
      data.studentCode || data.studentId
    }`,
    paddingX + 12,
    cursorY + 52
  );

  // Table
  cursorY += studentBoxHeight + 14;
  const colSttX = paddingX + 10;
  const colContentX = paddingX + 46;
  const colAmountRightX = paddingX + contentWidth - 12;

  ctx.fillStyle = '#f5f5f5';
  ctx.fillRect(paddingX, cursorY, contentWidth, tableHeaderHeight);
  ctx.strokeStyle = '#d9d9d9';
  ctx.strokeRect(paddingX, cursorY, contentWidth, tableHeaderHeight);

  ctx.fillStyle = '#111827';
  ctx.font = `700 12px ${fontFamily}`;
  ctx.textAlign = 'left';
  ctx.fillText('STT', colSttX, cursorY + 21);
  ctx.fillText('Khoản thu', colContentX, cursorY + 21);
  ctx.textAlign = 'right';
  ctx.fillText('Số tiền', colAmountRightX, cursorY + 21);

  cursorY += tableHeaderHeight;

  wrappedBills.forEach((item, idx) => {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(paddingX, cursorY, contentWidth, item.rowHeight);
    ctx.strokeStyle = '#e5e7eb';
    ctx.strokeRect(paddingX, cursorY, contentWidth, item.rowHeight);

    ctx.fillStyle = '#374151';
    ctx.font = `500 13px ${fontFamily}`;
    ctx.textAlign = 'left';
    ctx.fillText(String(idx + 1), colSttX + 4, cursorY + 21);

    ctx.fillStyle = '#111827';
    item.lines.forEach((line, lineIdx) => {
      ctx.fillText(line, colContentX, cursorY + 21 + lineIdx * 18);
    });

    ctx.font = `600 13px ${fontFamily}`;
    ctx.textAlign = 'right';
    ctx.fillText(formatCurrencyVnd(item.bill.cost), colAmountRightX, cursorY + 21);

    cursorY += item.rowHeight;
  });

  // Total Row
  ctx.fillStyle = '#fafafa';
  ctx.fillRect(paddingX, cursorY, contentWidth, tableTotalHeight);
  ctx.strokeStyle = '#d9d9d9';
  ctx.strokeRect(paddingX, cursorY, contentWidth, tableTotalHeight);

  ctx.fillStyle = '#111827';
  ctx.font = `700 13.5px ${fontFamily}`;
  ctx.textAlign = 'left';
  ctx.fillText('Tổng cộng', colSttX, cursorY + 24);
  ctx.font = `700 15px ${fontFamily}`;
  ctx.textAlign = 'right';
  ctx.fillText(formatCurrencyVnd(data.totalCost), colAmountRightX, cursorY + 24);

  cursorY += tableTotalHeight + 16;

  // QR Image
  const qrBoxSize = 220;
  const qrBoxX = (cardWidth - qrBoxSize) / 2;
  const qrBoxY = cursorY + 10;

  ctx.strokeStyle = '#e5e7eb';
  ctx.strokeRect(qrBoxX - 6, qrBoxY - 6, qrBoxSize + 12, qrBoxSize + 12);

  const qrImg = await loadImageFromBase64(data.qrBase64);
  ctx.drawImage(qrImg, qrBoxX, qrBoxY, qrBoxSize, qrBoxSize);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Không thể xuất ảnh PNG'));
      }
    }, 'image/png');
  });
};

export const downloadBlobAsFile = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 3000);
};

export const copySlipBlobToClipboard = async (blob: Blob): Promise<void> => {
  if (!navigator.clipboard || typeof ClipboardItem === 'undefined') {
    throw new Error('Trình duyệt không hỗ trợ copy ảnh vào Clipboard');
  }
  const item = new ClipboardItem({ 'image/png': blob });
  await navigator.clipboard.write([item]);
};

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
})();

const computeCrc32 = (bytes: Uint8Array): number => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = CRC32_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

export const createZipBlobFromFiles = (files: ZipEntryFile[]): Blob => {
  const encoder = new TextEncoder();
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = encoder.encode(file.fileName);
    const crc = computeCrc32(file.data);
    const size = file.data.length;

    const localHeader = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(localHeader.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(6, 0x0800, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, 0, true);
    localView.setUint16(12, 0x21, true);
    localView.setUint32(14, crc, true);
    localView.setUint32(18, size, true);
    localView.setUint32(22, size, true);
    localView.setUint16(26, nameBytes.length, true);
    localView.setUint16(28, 0, true);
    localHeader.set(nameBytes, 30);

    localParts.push(localHeader, file.data);

    const centralHeader = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(8, 0x0800, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint16(12, 0, true);
    centralView.setUint16(14, 0x21, true);
    centralView.setUint32(16, crc, true);
    centralView.setUint32(20, size, true);
    centralView.setUint32(24, size, true);
    centralView.setUint16(28, nameBytes.length, true);
    centralView.setUint16(30, 0, true);
    centralView.setUint16(32, 0, true);
    centralView.setUint16(34, 0, true);
    centralView.setUint16(36, 0, true);
    centralView.setUint32(38, 0, true);
    centralView.setUint32(42, offset, true);
    centralHeader.set(nameBytes, 46);

    centralParts.push(centralHeader);
    offset += localHeader.length + size;
  }

  const centralSize = centralParts.reduce((sum, p) => sum + p.length, 0);
  const eocd = new Uint8Array(22);
  const eocdView = new DataView(eocd.buffer);
  eocdView.setUint32(0, 0x06054b50, true);
  eocdView.setUint16(4, 0, true);
  eocdView.setUint16(6, 0, true);
  eocdView.setUint16(8, files.length, true);
  eocdView.setUint16(10, files.length, true);
  eocdView.setUint32(12, centralSize, true);
  eocdView.setUint32(16, offset, true);
  eocdView.setUint16(20, 0, true);

  const totalLength = offset + centralSize + eocd.length;
  const outBuffer = new ArrayBuffer(totalLength);
  const outBytes = new Uint8Array(outBuffer);
  let writePos = 0;

  for (const part of localParts) {
    outBytes.set(part, writePos);
    writePos += part.length;
  }
  for (const part of centralParts) {
    outBytes.set(part, writePos);
    writePos += part.length;
  }
  outBytes.set(eocd, writePos);

  return new Blob([outBuffer], {
    type: 'application/zip',
  });
};
